import { BaseDragger } from "./BaseDragger";
import { trackFeature } from "@/analytics/drawing-analytics";
import { DraggerType, type Point2D, type Point3D } from "./types";
import { PREVIEW_ORIGIN_STYLE } from "./previewStyles";

/** Radius of the origin marker in screen pixels */
const ORIGIN_RADIUS = 12;

/**
 * Dragger for setting the ruler origin point.
 *
 * On pointer down, shows a crosshair marker at the touch position.
 * On pointer up, sets the ruler origin to that world position.
 */
export class OriginDragger extends BaseDragger {
  readonly type = DraggerType.Origin;

  private markerIds: string[] = [];

  // ============== Lifecycle ==============

  protected onActivate(): void {
    // Nothing special on activate
  }

  protected onDeactivate(): void {
    this.clearMarker();
  }

  // ============== Drag Events ==============

  protected onStart(screen: Point2D): void {
    this.createMarker(screen);
  }

  protected onDrag(screen: Point2D): void {
    this.updateMarker(screen);
  }

  protected onEnd(_screen: Point2D, world: Point3D): void {
    trackFeature("ruler_moved", { via: "tool" });
    // Set ruler origin to the current world position
    if (this.viewer.overlayManager) {
      const origin = { x: world.x, y: world.y };

      // Emit a custom event that Facade can listen to
      this.viewer.canvas.dispatchEvent(
        new CustomEvent("originset", {
          detail: { origin },
          bubbles: true,
        }),
      );
    }

    this.clearMarker();
  }

  protected onCancel(): void {
    this.clearMarker();
    super.onCancel();
  }

  // ============== Marker Drawing ==============

  private createMarker(screen: Point2D): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    this.clearMarker();

    // Create circle marker
    const circleId = overlay.createCircle(
      screen.x,
      screen.y,
      ORIGIN_RADIUS,
      PREVIEW_ORIGIN_STYLE,
    );
    if (circleId) this.markerIds.push(circleId);

    // Create crosshairs
    const halfSize = ORIGIN_RADIUS * 0.7;

    // Vertical line
    const vLineId = overlay.createLine(
      screen.x,
      screen.y - halfSize,
      screen.x,
      screen.y + halfSize,
      PREVIEW_ORIGIN_STYLE,
    );
    if (vLineId) this.markerIds.push(vLineId);

    // Horizontal line
    const hLineId = overlay.createLine(
      screen.x - halfSize,
      screen.y,
      screen.x + halfSize,
      screen.y,
      PREVIEW_ORIGIN_STYLE,
    );
    if (hLineId) this.markerIds.push(hLineId);
  }

  private updateMarker(screen: Point2D): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay || this.markerIds.length === 0) return;

    // Update circle position
    if (this.markerIds[0]) {
      overlay.update(this.markerIds[0], { cx: screen.x, cy: screen.y });
    }

    // Update vertical line
    const halfSize = ORIGIN_RADIUS * 0.7;
    if (this.markerIds[1]) {
      overlay.update(this.markerIds[1], {
        x1: screen.x,
        y1: screen.y - halfSize,
        x2: screen.x,
        y2: screen.y + halfSize,
      });
    }

    // Update horizontal line
    if (this.markerIds[2]) {
      overlay.update(this.markerIds[2], {
        x1: screen.x - halfSize,
        y1: screen.y,
        x2: screen.x + halfSize,
        y2: screen.y,
      });
    }
  }

  private clearMarker(): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    for (const id of this.markerIds) {
      overlay.remove(id);
    }
    this.markerIds = [];
  }

  // ============== Cleanup ==============

  dispose(): void {
    this.clearMarker();
    super.dispose();
  }
}
